import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  AlertCircle,
  Check,
  ChevronRight,
  Inbox,
  Maximize2,
  Undo2,
  X,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { type Papel, Visor } from "@/components/propuesta/visor";
import { ColaDeBoletas } from "@/components/revision/cola-de-boletas";
import { Cotejo } from "@/components/revision/cotejo";
import { Lado } from "@/components/revision/lado";
import { TandaDelMotor } from "@/components/revision/tanda-del-motor";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { comoEstaLaCuenta, filasDelVoucher } from "@/lib/voucher";

/**
 * Los cuatro montones por dificultad. Los cortes viven en el motor
 * (`cruce/dificultad.ts`); aquí solo se nombran, que es lo que viaja por el
 * contrato.
 */
type Banda = "facil" | "normal" | "dificil" | "sin_candidato";

export const Route = createFileRoute("/_app/revision")({ component: Revision });

// ===========================================================================
// La revisión guiada (lámina 8).
//
// Las tres reglas de la propuesta llevadas al extremo: una sola cosa que hacer,
// de uno en uno, y lo que explica se pliega.
//
// Quien revisa por primera vez no necesita entender el sistema — necesita
// mirar dos cifras y responder una pregunta. Por eso la pantalla es una
// pregunta, dos tarjetas y tres botones, y todo lo demás está detrás de un
// desplegable.
//
// Con atajos de teclado (S / N / D): revisar 96 casos con el ratón es media
// hora de clics.
// ===========================================================================

function Revision() {
  const [indice, setIndice] = useState(0);
  // El papel que se está mirando en grande, o `null`. Uno solo: mirar dos a la
  // vez no es lo que se hace, se pasa de uno a otro.
  const [papel, setPapel] = useState<Papel | null>(null);
  /**
   * En qué orden se recorre la cola.
   *
   * Por importe se revisa primero lo que más pesa en el cierre; por duda,
   * aquello de lo que el motor está menos seguro. Las dos son formas legítimas
   * de repartir un rato de trabajo, así que lo elige quien revisa y no quien
   * programó. Se recuerda entre visitas: cambiarlo cada vez es una molestia
   * diaria por una decisión que se toma una vez.
   */
  /**
   * En qué cola se trabaja. Abre en BOLETAS porque es la que se acaba: son
   * catorce documentos de clientes contra ciento doce abonos, y son los que
   * impiden cerrar el mes.
   */
  const [cola, setCola] = useState<"boletas" | "abonos">("boletas");
  const [orden, setOrden] = useState<"importe" | "duda">(() => {
    try {
      return localStorage.getItem("revision.orden") === "duda"
        ? "duda"
        : "importe";
    } catch {
      return "importe";
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem("revision.orden", orden);
    } catch {
      // Una ventana privada no puede guardar y no pasa nada: se usa el
      // predeterminado y la pantalla funciona igual.
    }
  }, [orden]);

  /**
   * En qué montón se está trabajando, para poder ir de menor a mayor
   * dificultad. `undefined` = la cola entera, como estaba.
   */
  const [banda, setBanda] = useState<Banda | undefined>(undefined);

  const { data, isPending } = api.revision.useQuery(
    ["revision", String(indice), orden, banda ?? "todas"],
    {
      query: { indice, orden, banda },
    },
  );
  const d = data?.status === 200 ? data.body : null;
  const caso = d?.caso ?? null;

  // Si el periodo tiene algo dentro o está recién abierto.
  //
  // La cola vacía significa dos cosas opuestas —«ya terminaste» y «todavía no
  // empezaste»— y `total` vale 0 en las dos. La portada sí lo sabe, y su
  // consulta ya la trae el menú lateral cacheada, así que preguntarlo aquí no
  // añade una petición: solo distingue las dos frases.
  const { data: estado } = api.inicio.useQuery(["inicio"], undefined, {
    staleTime: 60_000,
  });
  const hayMovimientos =
    estado?.status === 200 ? estado.body.totalPagos > 0 : true;

  // Ya solo se pliega lo que EXPLICA. Los dos papeles —el voucher y la boleta—
  // salieron de aquí: eran el tercer y el cuarto plegable, abribles de uno en
  // uno, y son justo lo que contesta la pregunta de la pantalla.
  const [abierto, setAbierto] = useState<"porque" | "otros" | null>(null);
  const [ultimo, setUltimo] = useState<{ id: string; que: string } | null>(
    null,
  );
  const qc = useQueryClient();

  /**
   * Lo que hay que releer después de decidir, y solo eso.
   *
   * Antes cada decisión llamaba a `invalidateQueries()` SIN clave, que tira el
   * caché entero: el maestro de empresas, el tipo de cambio y la bitácora que
   * nadie ha tocado se volvían a pedir seis veces por caso. Lo que cambia al
   * decidir son dos cosas: la cola de esta pantalla y los contadores del
   * sidebar, que salen de `inicio`.
   */
  const refrescar = useCallback(() => {
    qc.invalidateQueries({ queryKey: ["revision"] });
    qc.invalidateQueries({ queryKey: ["inicio"] });
  }, [qc]);

  const decidir = api.decidir.useMutation();
  const deshacerMut = api.deshacer.useMutation();
  const corregir = api.corregir.useMutation();
  const recruzar = api.recruzar.useMutation();

  /**
   * La vía de escape: cuando el lector no pudo con la imagen, se escribe a mano.
   *
   * El sistema anterior la tenía y este la había perdido: sin ella, un voucher
   * borroso es un callejón sin salida —queda en «no sabría decir» y no avanza
   * nunca—. Y de 1.777 imágenes solo 227 traen número de operación leído, así
   * que no es un caso raro.
   */
  /**
   * Cuál de los candidatos se está mirando.
   *
   * Cero es el que propone el motor. Contabilidad pidió poder decir «este no,
   * enséñame el siguiente» sin tener que descartar el caso entero: descartar
   * es una decisión que queda escrita, y mirar otra opción no debería serlo.
   */
  const [iCandidato, setICandidato] = useState(0);
  const enCurso = caso?.candidatos?.[iCandidato] ?? null;
  const hayOtro = (caso?.candidatos?.length ?? 0) > iCandidato + 1;

  const [editando, setEditando] = useState(false);
  const [borrador, setBorrador] = useState<{
    fecha: string;
    operacion: string;
    importe: string;
    moneda: "PEN" | "USD";
  }>({ fecha: "", operacion: "", importe: "", moneda: "PEN" });

  /**
   * Al decidir NO se avanza el índice.
   *
   * La cola es «lo que queda por revisar», así que en cuanto uno se resuelve
   * los demás corren un puesto. Si además avanzáramos el índice, cada decisión
   * se saltaría el caso siguiente — y quien revisa noventa y seis no tiene
   * forma de notar que va viendo la mitad.
   */
  const responder = useCallback(
    (respuesta: "si" | "no" | "no_se") => {
      if (!caso || decidir.isPending) return;
      const id = caso.id;
      // Se confirma EXACTAMENTE la boleta que se estaba mirando. Dejar que el
      // servidor la vuelva a elegir abre la puerta a que confirme una distinta
      // de la que se enseñó, que es la peor forma posible de equivocarse: la
      // decisión quedaría firmada con el nombre de quien no la tomó.
      decidir.mutate(
        {
          body: {
            movimientoId: id,
            // La boleta que se está mirando AHORA, que puede no ser la que
            // propuso el motor: si alguien pasó al siguiente candidato, es esa
            // la que tiene delante y esa es la que firma.
            comprobanteId: enCurso?.comprobanteId ?? caso.comprobanteId,
            respuesta,
          },
        },
        {
          onSuccess: (r: { status: number; body: { dice?: string } }) => {
            if (r.status !== 200) {
              toast.error("No se pudo guardar la decisión");
              return;
            }
            const dice = r.body.dice ?? "Guardado";
            setUltimo({ id, que: dice });
            toast.success(dice);
            refrescar();
          },
          onError: () => toast.error("No se pudo guardar la decisión"),
        },
      );
    },
    [caso, decidir, enCurso, refrescar],
  );

  const deshacer = useCallback(() => {
    if (!ultimo) return;
    deshacerMut.mutate(
      { body: { movimientoId: ultimo.id } },
      {
        onSuccess: () => {
          toast.success("Deshecho");
          setUltimo(null);
          refrescar();
        },
        onError: () => toast.error("No se pudo deshacer"),
      },
    );
  }, [ultimo, deshacerMut, refrescar]);

  const siguiente = useCallback(() => setIndice((i) => i + 1), []);

  // Al cambiar de caso se vuelve al candidato que propone el motor: quedarse
  // en «el tercero» del caso anterior enseñaría una boleta que nadie eligió.
  // biome-ignore lint/correctness/useExhaustiveDependencies: el reset depende del caso, no del índice
  useEffect(() => {
    setICandidato(0);
  }, [caso?.id]);

  // Al cambiar de caso se cierra la edición. Heredar el borrador de un voucher
  // en el siguiente sería escribir los datos de uno encima de otro, firmado con
  // el nombre de quien no lo miró.
  // biome-ignore lint/correctness/useExhaustiveDependencies: depende del caso, no del id
  useEffect(() => {
    setEditando(false);
  }, [caso?.id, caso?.recorte]);

  const abrirEdicion = useCallback(() => {
    if (!caso?.voucher) return;
    setBorrador({
      fecha: caso.voucher.cuando ?? "",
      operacion: caso.voucher.operacion ?? "",
      importe:
        caso.voucher.importe != null
          ? (caso.voucher.importe / 100).toFixed(2)
          : "",
      moneda: caso.voucher.moneda ?? "PEN",
    });
    setEditando(true);
  }, [caso]);

  const guardarCorreccion = useCallback(() => {
    if (!caso?.recorte) return;
    const centimos = Math.round(
      Number(borrador.importe.replace(",", ".")) * 100,
    );
    if (!Number.isFinite(centimos) || centimos < 0) {
      toast.error("Ese importe no se entiende");
      return;
    }
    corregir.mutate(
      {
        body: {
          recorte: caso.recorte,
          veredicto: "corregido",
          fecha: borrador.fecha || null,
          operacion: borrador.operacion.trim() || null,
          importe: centimos,
          moneda: borrador.moneda,
        },
      },
      {
        onSuccess: (r: { status: number; body: { dice?: string } }) => {
          if (r.status !== 200) {
            toast.error("No se pudo guardar la corrección");
            return;
          }
          setEditando(false);
          refrescar();

          // Corregir sin volver a cruzar deja el trabajo a medias: el dato
          // bueno está guardado y el cruce sigue siendo el que se hizo con el
          // dato malo. Se ofrece cerrarlo aquí mismo — la pasada sobre el mes
          // entero tarda un segundo.
          toast.success(r.body.dice ?? "Corregido", {
            action: {
              label: "Volver a cruzar",
              onClick: () =>
                recruzar.mutate(
                  { body: { confirmo: true } },
                  {
                    onSuccess: (x: { body: { dice?: string } }) => {
                      toast.success(`Cruzado de nuevo: ${x.body.dice ?? ""}`);
                      refrescar();
                    },
                    onError: () => toast.error("No se pudo volver a cruzar"),
                  },
                ),
            },
            duration: 10_000,
          });
        },
        onError: () => toast.error("No se pudo guardar la corrección"),
      },
    );
  }, [caso, borrador, corregir, recruzar, refrescar]);

  const marcarIlegible = useCallback(() => {
    if (!caso?.recorte) return;
    corregir.mutate(
      { body: { recorte: caso.recorte, veredicto: "ilegible" } },
      {
        onSuccess: () => {
          toast.success("Anotado: la imagen no se puede leer");
          setEditando(false);
          refrescar();
        },
        onError: () => toast.error("No se pudo guardar"),
      },
    );
  }, [caso, corregir, refrescar]);

  // Atajos: S sí, N no, D no sé. Se ignoran si alguien está escribiendo.
  useEffect(() => {
    const alPulsar = (e: KeyboardEvent) => {
      const dentroDeUnCampo =
        e.target instanceof HTMLElement &&
        ["INPUT", "TEXTAREA", "SELECT"].includes(e.target.tagName);
      if (dentroDeUnCampo || e.metaKey || e.ctrlKey) return;
      const k = e.key.toLowerCase();
      if (k === "s") {
        e.preventDefault();
        responder("si");
      } else if (k === "n") {
        e.preventDefault();
        responder("no");
      } else if (k === "d") {
        e.preventDefault();
        responder("no_se");
      } else if (k === "z" && ultimo) {
        e.preventDefault();
        deshacer();
      }
    };
    window.addEventListener("keydown", alPulsar);
    return () => window.removeEventListener("keydown", alPulsar);
  }, [responder, deshacer, ultimo]);

  const dinero = (c: number, m: "PEN" | "USD") =>
    `${m === "USD" ? "US$" : "S/"} ${(c / 100).toLocaleString("es-PE", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  return (
    <>
      {/* Cabecera mínima: dónde estoy y cómo salgo. */}
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2 text-[12.5px] text-muted-foreground">
          Conciliación <span className="text-border">/</span>
          <b className="font-semibold text-[19px] text-foreground tracking-tight">
            Revisión guiada
          </b>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {/* LAS DOS COLAS.
              La de BOLETAS es la que se acaba: son los documentos de clientes
              que falta vincular, catorce, y son los que impiden cerrar el mes.
              La de ABONOS es la de siempre y también es trabajo, pero de otra
              clase: un abono sin documento se resuelve pidiéndole el expediente
              a finanzas, no buscándole el pago.
              Se abre en boletas porque es lo finito. */}
          <div className="flex overflow-hidden rounded-md border text-[11.5px]">
            {(
              [
                ["boletas", "Por boleta"],
                ["abonos", "Por abono"],
              ] as const
            ).map(([valor, texto]) => (
              <button
                key={valor}
                type="button"
                onClick={() => setCola(valor)}
                className={cn(
                  "px-2.5 py-1 transition-colors",
                  cola === valor
                    ? "bg-foreground font-medium text-background"
                    : "text-muted-foreground hover:bg-muted",
                )}
              >
                {texto}
              </button>
            ))}
          </div>
          {/* Por dónde empezar. Contabilidad pidió «lo dudoso primero» y hasta
              ahora la cola solo sabía ordenar por importe. Las dos sirven, y
              cuál conviene depende del día: se elige aquí. */}
          <div className="flex overflow-hidden rounded-md border text-[11.5px]">
            {(
              [
                ["importe", "Lo que más pesa"],
                ["duda", "Lo más dudoso"],
              ] as const
            ).map(([valor, texto]) => (
              <button
                key={valor}
                type="button"
                onClick={() => {
                  setOrden(valor);
                  setIndice(0);
                }}
                className={cn(
                  "px-2.5 py-1 transition-colors",
                  orden === valor
                    ? "bg-foreground font-medium text-background"
                    : "text-muted-foreground hover:bg-muted",
                )}
              >
                {texto}
              </button>
            ))}
          </div>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/movimientos">Ver la lista</Link>
          </Button>
        </div>
      </div>

      {/* La cola por boleta reemplaza TODO lo de abajo: es otra pregunta —«¿qué
          abono pagó esta boleta?» en vez de «¿qué boleta pagó este abono?»— y
          mezclar las dos pantallas confundiría cuál se está respondiendo. */}
      {cola === "boletas" ? <ColaDeBoletas /> : null}

      {cola === "abonos" ? (
        <>
          {/* Lo que el motor da por cierto, que ya NO se aplica solo. Va arriba
          del todo porque es el trabajo más barato del día: doscientas líneas
          obvias que se revisan de un vistazo y se firman de una vez. */}
          <TandaDelMotor />

          {/* POR DIFICULTAD, para poder ir de menor a mayor.
          Los cortes no son los que pidió el área —«mayores a 80»— porque
          ningún candidato de la base pasa de 0,65: lo fácil ya lo cierra el
          motor y no llega aquí. Y hay un cuarto montón que el esquema de tres
          no nombraba, «sin propuesta», que es el grueso del trabajo. */}
          {d ? (
            <div className="flex flex-wrap items-center gap-1.5 border-b pb-2 text-[12px]">
              <span className="mr-1 text-muted-foreground">Trabajar en</span>
              {(
                [
                  [
                    undefined,
                    "Todo",
                    d.bandas.facil + d.bandas.normal + d.bandas.dificil,
                  ],
                  ["facil", "Fácil", d.bandas.facil],
                  ["normal", "Normal", d.bandas.normal],
                  ["dificil", "Difícil", d.bandas.dificil],
                  ["sin_candidato", "Sin propuesta", d.bandas.sinCandidato],
                ] as const
              ).map(([valor, texto, cuantos]) => (
                <button
                  key={texto}
                  type="button"
                  onClick={() => {
                    setBanda(valor as Banda | undefined);
                    setIndice(0);
                  }}
                  className={cn(
                    "flex items-center gap-1.5 rounded-md border px-2.5 py-1 transition-colors",
                    banda === valor
                      ? "border-foreground bg-foreground font-medium text-background"
                      : "text-muted-foreground hover:bg-muted",
                  )}
                >
                  {texto}
                  <span className="tabular-nums opacity-70">{cuantos}</span>
                </button>
              ))}
              {banda === "sin_candidato" ? (
                <span className="ml-2 text-[11.5px] text-muted-foreground">
                  ningún voucher los explica: falta subir su documento, o no son
                  el cobro de una boleta
                </span>
              ) : null}
            </div>
          ) : null}

          {isPending ? (
            <Skeleton className="mx-auto h-96 w-full max-w-6xl" />
          ) : !caso ? (
            <div className="mx-auto max-w-lg py-24 text-center">
              {hayMovimientos ? (
                <>
                  <Check className="mx-auto size-10 text-emerald-600" />
                  <h2 className="mt-4 font-semibold text-2xl">
                    No queda nada por revisar
                  </h2>
                  <p className="mt-2 text-muted-foreground">
                    {d?.total
                      ? "Ya llegaste al final de la cola."
                      : "La bandeja está vacía."}
                  </p>
                  <Button className="mt-6" asChild>
                    <Link to="/">Volver al inicio</Link>
                  </Button>
                </>
              ) : (
                // Con el periodo vacío salía el visto verde y «no queda nada por
                // revisar», que es la pantalla de haber terminado el mes. Es la
                // frase contraria a la verdad el día que se estrena un periodo.
                <>
                  <Inbox className="mx-auto size-10 text-muted-foreground/50" />
                  <h2 className="mt-4 font-semibold text-2xl">
                    No hay nada que revisar todavía
                  </h2>
                  <p className="mt-2 text-muted-foreground">
                    La revisión guiada pregunta de a un abono con su voucher al
                    lado. Hasta que no se carguen los estados de cuenta y los
                    expedientes, no hay nada que preguntar.
                  </p>
                  <Button className="mt-6" asChild>
                    <Link to="/carga">Cargar el mes</Link>
                  </Button>
                </>
              )}
            </div>
          ) : (
            <div className="mx-auto w-full max-w-6xl">
              {/* Progreso: cuántos van y cuánto queda. */}
              <div className="flex items-center gap-4">
                <span className="shrink-0 text-[12.5px]">
                  <b>{(indice + 1).toLocaleString("es-PE")}</b>
                  <span className="text-muted-foreground">
                    {" "}
                    de {d?.total.toLocaleString("es-PE")}
                  </span>
                </span>
                <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full rounded-full bg-cyan-500"
                    style={{
                      width: `${(((indice + 1) / Math.max(d?.total ?? 1, 1)) * 100).toFixed(2)}%`,
                    }}
                  />
                </div>
                <span className="shrink-0 text-[12px] text-muted-foreground">
                  quedan unos{" "}
                  {Math.max(1, Math.round(((d?.total ?? 0) - indice) / 5))}{" "}
                  minutos
                </span>
              </div>

              {/* La pregunta. */}
              <div className="mt-10 text-center">
                <h2 className="font-semibold text-3xl tracking-tight">
                  {caso.pregunta}
                </h2>
                <p className="mx-auto mt-3 max-w-2xl text-muted-foreground">
                  {caso.porQue}
                </p>
              </div>

              {/* Los dos lados. */}
              <div className="mt-8 grid gap-4 md:grid-cols-2">
                <Lado
                  titulo="Entró en el banco"
                  importe={dinero(caso.banco.importe, caso.banco.moneda)}
                  /* Quién depositó, según el banco. Va en la tarjeta y no en un
                 plegable porque en los pagos por celular —que no traen número
                 de operación— es lo ÚNICO con lo que se puede responder. */
                  nota={
                    caso.banco.nombreEnLaGlosa
                      ? {
                          texto: caso.banco.nombreEnLaGlosa.fuerte
                            ? `El banco nombra a este cliente: ${caso.banco.nombreEnLaGlosa.casados.join(", ")}`
                            : `El banco solo coincide en ${caso.banco.nombreEnLaGlosa.casados.join(", ")}`,
                          tono: caso.banco.nombreEnLaGlosa.fuerte
                            ? "ok"
                            : "warn",
                        }
                      : null
                  }
                  filas={[
                    [
                      "Cuándo",
                      // La hora, cuando el banco la escribe. Solo lo hace el BCP,
                      // que es justo el que no escribe nombres: entre cuatro
                      // abonos iguales del mismo día es lo único que los separa.
                      caso.banco.hora
                        ? `${fecha(caso.banco.cuando)} · ${caso.banco.hora}`
                        : fecha(caso.banco.cuando),
                    ],
                    ["Dónde", caso.banco.donde],
                    ["N.º operación", caso.banco.operacion ?? "—"],
                    ["Concepto", caso.banco.concepto],
                  ]}
                  resaltar={
                    caso.voucher?.comparacion !== "identico"
                      ? "N.º operación"
                      : undefined
                  }
                />
                {caso.voucher && editando ? (
                  <Corregir
                    borrador={borrador}
                    alCambiar={setBorrador}
                    alGuardar={guardarCorreccion}
                    alCancelar={() => setEditando(false)}
                    alMarcarIlegible={marcarIlegible}
                    guardando={corregir.isPending}
                  />
                ) : caso.voucher ? (
                  <Lado
                    titulo="Dice el voucher del cliente"
                    etiqueta={enCurso?.medio ?? caso.voucher.medio}
                    origen={caso.voucher.origen}
                    alEditar={caso.recorte ? abrirEdicion : undefined}
                    /* «—» y no «S/ 0.00» cuando el papel no trae importe leído.
                   Un cero es una cifra: dice que el voucher pagó nada, y lo
                   que pasa es que no se pudo leer cuánto. La cifra grande de
                   la tarjeta es lo primero que se mira para responder. */
                    importe={
                      caso.voucher.importe === null
                        ? "—"
                        : dinero(
                            caso.voucher.importe,
                            caso.voucher.moneda ?? "PEN",
                          )
                    }
                    /* Si la hora del voucher es la que el banco asentó. Se dice
                   el hecho y no un veredicto, igual que con el nombre: puede
                   coincidir por casualidad, y puede no coincidir porque la
                   lectura se comió el p.m. */
                    nota={
                      caso.voucher.horaCuadra === null
                        ? null
                        : caso.voucher.horaCuadra
                          ? {
                              texto: `El banco asentó a esa misma hora (${caso.banco.hora})`,
                              tono: "ok" as const,
                            }
                          : {
                              texto: `El banco lo asentó a las ${caso.banco.hora}, no a las ${caso.voucher.hora}`,
                              tono: "warn" as const,
                            }
                    }
                    /* Las filas las dice `filasDelVoucher`, no esta pantalla.
                   Antes se armaban a mano aquí y a mano en la ficha, y por eso
                   cada una enseñaba campos distintos: aquí salían el medio, la
                   hora y quién pagó, y allá no; y el banco y la cuenta de
                   destino no salían en ninguna de las dos, estando leídos.

                   Lo que se corrige a mano —`enCurso`— se mezcla ANTES de
                   pedirlas: la lista es la misma, los valores son los que la
                   persona está escribiendo. */
                    filas={[
                      ...filasDelVoucher({
                        ...caso.voucher,
                        operacion: enCurso?.operacion ?? caso.voucher.operacion,
                        medio: enCurso?.medio ?? caso.voucher.medio,
                      }),
                      ["Boleta", enCurso?.boleta ?? caso.voucher.boleta ?? "—"],
                      [
                        "Cliente",
                        enCurso?.cliente ?? caso.voucher.cliente ?? "—",
                      ],
                    ]}
                    /* Si la cuenta a la que entró el dinero es del grupo o no. Lo
                   pidió el área y ya estaba resuelto en la base contra el
                   maestro: 65 de 92 cuentas leídas resuelven, 27 no. */
                    cuenta={comoEstaLaCuenta(caso.voucher)}
                    resaltar={
                      caso.voucher.comparacion !== "identico"
                        ? "N.º operación"
                        : undefined
                    }
                  />
                ) : (
                  <div className="flex items-center justify-center rounded-lg border border-dashed p-8 text-center text-muted-foreground text-sm">
                    Ningún voucher leído tiene este importe.
                  </div>
                )}

                {/* EL CLIENTE, SEGÚN LA FICHA DEL EXPEDIENTE.
                Lo pidió contabilidad, y va aquí y no detrás de un plegable
                porque es parte de la comparación: la boleta dice quién firma,
                la ficha dice cómo localizarlo. El teléfono es el que más
                desempata —un Yape no trae operación ni cuenta— así que va
                destacado y en monoespaciado, para poder cotejarlo dígito a
                dígito con el que enseña la imagen. */}
                {caso.ficha ? (
                  <div className="mt-3 rounded-lg border bg-muted/30 p-3 text-[12.5px]">
                    <div className="mb-2 flex flex-wrap items-center gap-2">
                      <span className="font-medium text-muted-foreground">
                        El cliente, según la ficha
                      </span>
                      {caso.ficha.cuadraConLaBoleta ? null : (
                        <span className="rounded border border-amber-500/40 bg-amber-500/10 px-1.5 text-[11px] text-amber-700 dark:text-amber-400">
                          no es el mismo nombre que la boleta
                        </span>
                      )}
                      {caso.ficha.archivoId && caso.ficha.pagina ? (
                        <a
                          className="ml-auto text-[11.5px] underline underline-offset-2 hover:no-underline"
                          href={`/pagina/${caso.ficha.archivoId}/${caso.ficha.pagina}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Abrir la hoja
                        </a>
                      ) : null}
                    </div>
                    <dl className="grid gap-x-4 gap-y-1 sm:grid-cols-[auto_1fr]">
                      <dt className="text-muted-foreground">Nombre</dt>
                      <dd>{caso.ficha.nombre}</dd>
                      {caso.ficha.documento ? (
                        <>
                          <dt className="text-muted-foreground">Documento</dt>
                          <dd className="font-mono">{caso.ficha.documento}</dd>
                        </>
                      ) : null}
                      {caso.ficha.telefonos.length > 0 ? (
                        <>
                          <dt className="text-muted-foreground">
                            {caso.ficha.telefonos.length > 1
                              ? "Teléfonos"
                              : "Teléfono"}
                          </dt>
                          <dd className="font-mono">
                            {caso.ficha.telefonos.join(" · ")}
                          </dd>
                        </>
                      ) : null}
                      {caso.ficha.distrito ? (
                        <>
                          <dt className="text-muted-foreground">Distrito</dt>
                          <dd>{caso.ficha.distrito}</dd>
                        </>
                      ) : null}
                      {caso.ficha.ocupacion ? (
                        <>
                          <dt className="text-muted-foreground">Ocupación</dt>
                          <dd>{caso.ficha.ocupacion}</dd>
                        </>
                      ) : null}
                    </dl>
                  </div>
                ) : null}
              </div>

              {/* EL COTEJO, dato por dato.
              Las dos columnas de arriba enseñan los dos papeles y dejan la
              comparación en los ojos de quien revisa. Con una glosa de sesenta
              caracteres y dos números de operación de siete dígitos, eso es
              pedirle que haga de lector óptico: en el primer caso real que se
              miró, el banco decía 5394085 y el voucher 5394081 —el último
              dígito— y el resumen decía «dicen lo mismo». */}
              <Cotejo filas={caso.cotejo} />

              {/* Pasar al siguiente candidato sin descartar nada.
              Contabilidad lo pidió así: mirar otra opción no es una decisión y
              no tiene por qué quedar escrita. Descartar sí, y para eso está
              «No es este». */}
              {caso.candidatos.length > 1 ? (
                <div className="mt-4 flex flex-wrap items-center gap-3 rounded-md border bg-muted/40 px-3 py-2 text-[12.5px]">
                  <span className="text-muted-foreground">
                    Candidato {iCandidato + 1} de {caso.candidatos.length}
                    {enCurso?.puntaje ? (
                      <span className="ml-1.5 tabular-nums">
                        · evidencia {puntos(enCurso.puntaje)} de 100
                      </span>
                    ) : null}
                  </span>
                  {enCurso?.yaTomada ? (
                    <span className="rounded border border-amber-500/40 bg-amber-500/10 px-1.5 text-amber-700 dark:text-amber-400">
                      esa boleta ya está cobrada por otro abono
                    </span>
                  ) : null}
                  <Button
                    size="sm"
                    variant="outline"
                    className="ml-auto"
                    disabled={!hayOtro}
                    onClick={() => setICandidato((i) => i + 1)}
                  >
                    Ver el siguiente candidato
                  </Button>
                  {iCandidato > 0 ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setICandidato(0)}
                    >
                      Volver al propuesto
                    </Button>
                  ) : null}
                </div>
              ) : null}

              {/* LOS DOS PAPELES, A LA VISTA Y ANTES DE RESPONDER.
              La pregunta es «¿este abono es el pago de esa boleta?», y los dos
              documentos que la contestan estaban detrás del tercer y cuarto
              plegable —abribles de uno en uno—, así que se decidía sin haber
              mirado ninguno. Aquí van juntos: el voucher a la izquierda, la
              boleta emitida a la derecha, del mismo tamaño para poder pasar la
              vista de uno a otro. */}
              <div className="mt-6 grid gap-4 lg:grid-cols-2">
                <figure className="flex flex-col rounded-lg border bg-card p-3">
                  <figcaption className="mb-2 font-semibold text-[11px] text-muted-foreground uppercase tracking-wider">
                    El voucher, tal cual llegó
                  </figcaption>
                  {caso.recorte ? (
                    /* Se abre en el visor. A 460 px de alto un voucher de móvil no
                   se lee: el número de operación son seis dígitos de 8 px, y
                   quien revisa acaba decidiendo por el importe. */
                    <button
                      type="button"
                      onClick={() =>
                        setPapel({
                          clase: "imagen",
                          recorte: caso.recorte as string,
                          titulo: "El voucher, tal cual llegó",
                        })
                      }
                      className="group relative cursor-zoom-in rounded-md"
                      title="Agrandar el voucher"
                    >
                      <img
                        src={`/imagen/${caso.recorte}`}
                        alt="El voucher tal cual llegó"
                        className="max-h-[460px] w-full rounded-md border object-contain"
                      />
                      <span className="absolute right-2 bottom-2 flex items-center gap-1 rounded bg-black/70 px-1.5 py-0.5 text-[10.5px] text-white opacity-0 transition-opacity group-hover:opacity-100">
                        <Maximize2 className="size-3" /> agrandar
                      </span>
                    </button>
                  ) : (
                    <p className="flex flex-1 items-center justify-center rounded-md border border-dashed p-8 text-center text-muted-foreground text-sm">
                      De este pago no se guardó una imagen que mirar.
                    </p>
                  )}
                </figure>

                <figure className="flex flex-col rounded-lg border bg-card p-3">
                  <figcaption className="mb-2 flex flex-wrap items-baseline gap-2">
                    <span className="font-semibold text-[11px] text-muted-foreground uppercase tracking-wider">
                      El documento emitido
                    </span>
                    {caso.documento?.total != null ? (
                      <span className="text-[11.5px] text-muted-foreground">
                        la boleta de{" "}
                        {dinero(
                          caso.documento.total,
                          caso.documento.moneda ?? "PEN",
                        )}
                      </span>
                    ) : null}
                  </figcaption>
                  {caso.documento ? (
                    <>
                      {/* LOS TRES IMPORTES, no dos.
                      Esto comparaba la boleta con el banco y dejaba fuera el
                      voucher, que es el papel que trae el cliente. De 136
                      filas con importe y boleta, 77 no cuadran — y no todas
                      son un error: 47 son pagos a cuenta, que son normales.
                      Por eso se dice QUÉ SITUACIÓN ES y no si está mal. */}
                      {caso.cuadre.clase !== "cuadra" &&
                      caso.cuadre.clase !== "sin_voucher" ? (
                        <p
                          className={cn(
                            "mb-2 rounded px-2 py-1.5 text-[12.5px]",
                            caso.cuadre.avisa
                              ? "bg-amber-500/15 text-amber-800 dark:text-amber-300"
                              : "bg-muted text-muted-foreground",
                          )}
                        >
                          {caso.cuadre.dice}
                        </p>
                      ) : null}
                      {/* Del expediente se enseñaba UNA hoja de las cinco que
                      trae de media: la del comprobante. La ficha del cliente y
                      el voucher están en las otras, y no había forma de
                      llegar. El visor las recorre. */}
                      <button
                        type="button"
                        onClick={() =>
                          caso.documento &&
                          setPapel({
                            clase: "expediente",
                            archivoId: caso.documento.archivoId,
                            pagina: caso.documento.pagina,
                            paginas: caso.documento.paginas,
                            titulo: "El expediente del cliente",
                          })
                        }
                        className="group relative cursor-zoom-in rounded-md"
                        title="Ver el expediente completo"
                      >
                        <img
                          src={`/pagina/${caso.documento.archivoId}/${caso.documento.pagina}`}
                          alt="La boleta o factura emitida"
                          className="max-h-[460px] w-full rounded-md border bg-white object-contain"
                        />
                        <span className="absolute right-2 bottom-2 flex items-center gap-1 rounded bg-black/70 px-1.5 py-0.5 text-[10.5px] text-white opacity-0 transition-opacity group-hover:opacity-100">
                          <Maximize2 className="size-3" />
                          {caso.documento.paginas && caso.documento.paginas > 1
                            ? `ver las ${caso.documento.paginas} hojas`
                            : "agrandar"}
                        </span>
                      </button>
                    </>
                  ) : (
                    <p className="flex flex-1 items-center justify-center rounded-md border border-dashed p-8 text-center text-muted-foreground text-sm">
                      El expediente de esta boleta es un escaneo entero y no se
                      encontró la página del comprobante.
                    </p>
                  )}
                </figure>
              </div>

              {/* Las tres respuestas, con su atajo. */}
              <div className="mt-6 grid gap-3 sm:grid-cols-3">
                <Respuesta
                  icono={<Check className="size-5" />}
                  titulo="Sí, es este"
                  nota="Se concilia y pasa al siguiente"
                  tecla="S"
                  tono="ok"
                  alPulsar={() => responder("si")}
                  inhabilitado={!caso.voucher || decidir.isPending}
                />
                <Respuesta
                  icono={<X className="size-5" />}
                  titulo="No es este"
                  nota="Se buscan otros candidatos"
                  tecla="N"
                  tono="bad"
                  alPulsar={() => responder("no")}
                  inhabilitado={decidir.isPending}
                />
                <Respuesta
                  icono={<AlertCircle className="size-5" />}
                  titulo="No sabría decir"
                  nota="Queda pendiente para contabilidad"
                  tecla="D"
                  tono="neutro"
                  alPulsar={() => responder("no_se")}
                  inhabilitado={decidir.isPending}
                />
              </div>

              {/* Lo que explica, plegado. Se abre uno cada vez: dos abiertos a la
              vez empujan los botones fuera de la pantalla, y los botones son lo
              único que hay que poder alcanzar siempre. */}
              <div className="mt-4 space-y-2">
                <Plegable
                  titulo="Por qué te lo pregunto"
                  nota="la regla que frenó el cierre"
                  abierto={abierto === "porque"}
                  alAbrir={() =>
                    setAbierto(abierto === "porque" ? null : "porque")
                  }
                >
                  <p>{caso.porQue}</p>
                  <p className="mt-2 text-muted-foreground">
                    El sistema no cierra nada que no pueda demostrar. Cuando el
                    número de operación coincide en los dos papeles lo cierra
                    solo; cuando no, pregunta.
                  </p>
                </Plegable>

                <Plegable
                  titulo={
                    caso.otrosCandidatos > 0
                      ? `Ver los otros ${caso.otrosCandidatos} candidatos`
                      : "Buscar el voucher a mano"
                  }
                  nota={
                    caso.otrosCandidatos > 0
                      ? "con el mismo importe"
                      : "no hay otro con ese importe"
                  }
                  abierto={abierto === "otros"}
                  alAbrir={() =>
                    setAbierto(abierto === "otros" ? null : "otros")
                  }
                >
                  {caso.candidatos.length > 1 ? (
                    <ul className="space-y-1.5">
                      {caso.candidatos
                        .slice(1)
                        .map((c: (typeof caso.candidatos)[number]) => (
                          <li
                            key={`${c.comprobanteId}${c.operacion}`}
                            className="flex flex-wrap gap-x-3"
                          >
                            <b>{c.boleta ?? "—"}</b>
                            <span className="text-muted-foreground">
                              {c.cliente ?? "—"}
                            </span>
                            {c.medio ? (
                              <span className="rounded border px-1.5 text-[10.5px] text-muted-foreground">
                                {c.medio}
                              </span>
                            ) : null}
                            <span className="ml-auto font-mono text-[11.5px]">
                              {c.operacion ?? "—"}
                            </span>
                            <span className="text-muted-foreground">
                              {c.cuando ? fecha(c.cuando) : "—"}
                            </span>
                            <span className="text-[11px] text-muted-foreground tabular-nums">
                              {puntos(c.puntaje)}
                            </span>
                            {c.yaTomada ? (
                              <span className="text-[10.5px] text-amber-700 dark:text-amber-400">
                                ya cobrada
                              </span>
                            ) : null}
                          </li>
                        ))}
                    </ul>
                  ) : (
                    <p className="text-muted-foreground">
                      Ningún otro voucher leído tiene este importe. Se puede
                      buscar en la lista completa por importe, cliente o número.
                    </p>
                  )}
                </Plegable>
              </div>

              {/* Pie: qué implica lo que se decida. */}
              <div className="mt-10 flex flex-wrap items-center gap-3 border-t pt-4">
                <p className="text-[12.5px] text-muted-foreground">
                  Lo que decidas queda firmado con tu nombre y la hora ·{" "}
                  <b className="text-foreground">se puede deshacer</b>
                </p>
                <div className="ml-auto flex items-center gap-2">
                  {ultimo ? (
                    <Button variant="outline" size="sm" onClick={deshacer}>
                      <Undo2 className="mr-2 size-3.5" />
                      Deshacer lo último
                      <kbd className="ml-2 rounded border px-1 font-mono text-[10px]">
                        Z
                      </kbd>
                    </Button>
                  ) : null}
                  <Button variant="ghost" size="sm" onClick={siguiente}>
                    Saltar este →
                  </Button>
                  <Button variant="outline" size="sm" asChild>
                    <Link to="/">Dejar la revisión para luego</Link>
                  </Button>
                </div>
              </div>
            </div>
          )}
        </>
      ) : null}

      {/* El papel en grande. Va al final y fuera de la tarjeta: es un diálogo a
          pantalla completa, no una parte de la comparación. */}
      <Visor papel={papel} alCerrar={() => setPapel(null)} />
    </>
  );
}

/**
 * Escribir a mano lo que el lector no pudo leer.
 *
 * Solo el lado del VOUCHER se corrige. El del extracto lo dice el banco y no se
 * toca: si un abono del extracto se pudiera editar, el sistema dejaría de poder
 * cuadrar contra el papel del banco, que es la única verdad que no depende de
 * nosotros.
 *
 * Lo que se escribe aquí vale para TODAS las boletas que comparten esa imagen:
 * un voucher que aparece dieciocho veces no son dieciocho pagos.
 */
function Corregir({
  borrador,
  alCambiar,
  alGuardar,
  alCancelar,
  alMarcarIlegible,
  guardando,
}: {
  borrador: {
    fecha: string;
    operacion: string;
    importe: string;
    moneda: "PEN" | "USD";
  };
  alCambiar: (b: {
    fecha: string;
    operacion: string;
    importe: string;
    moneda: "PEN" | "USD";
  }) => void;
  alGuardar: () => void;
  alCancelar: () => void;
  alMarcarIlegible: () => void;
  guardando: boolean;
}) {
  const campo =
    "w-full rounded border bg-background px-2 py-1 text-[12.5px] tabular-nums";
  return (
    <div className="rounded-lg border-2 border-cyan-500/50 bg-card p-5 shadow-sm">
      <div className="font-semibold text-[11px] text-muted-foreground uppercase tracking-wider">
        Lo que dice el voucher, escrito a mano
      </div>
      <div className="mt-3 space-y-2.5 text-[12.5px]">
        <label className="block">
          <span className="text-muted-foreground">Cuándo</span>
          <input
            type="date"
            className={campo}
            value={borrador.fecha}
            onChange={(e) => alCambiar({ ...borrador, fecha: e.target.value })}
          />
        </label>
        <label className="block">
          <span className="text-muted-foreground">N.º de operación</span>
          <input
            className={`${campo} font-mono`}
            value={borrador.operacion}
            placeholder="tal como se lee en el papel"
            onChange={(e) =>
              alCambiar({ ...borrador, operacion: e.target.value })
            }
          />
        </label>
        <div className="flex gap-2">
          <label className="flex-1">
            <span className="text-muted-foreground">Importe</span>
            <input
              className={campo}
              inputMode="decimal"
              value={borrador.importe}
              onChange={(e) =>
                alCambiar({ ...borrador, importe: e.target.value })
              }
            />
          </label>
          <label className="w-28">
            <span className="text-muted-foreground">Moneda</span>
            <select
              className={campo}
              value={borrador.moneda}
              onChange={(e) =>
                alCambiar({
                  ...borrador,
                  moneda: e.target.value === "USD" ? "USD" : "PEN",
                })
              }
            >
              <option value="PEN">Soles</option>
              <option value="USD">Dólares</option>
            </select>
          </label>
        </div>
      </div>

      <p className="mt-3 text-[11px] text-muted-foreground leading-relaxed">
        Queda firmado con tu nombre y la hora, y vale para todas las boletas que
        adjuntan esta misma imagen. El lector no la vuelve a tocar.
      </p>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button size="sm" onClick={alGuardar} disabled={guardando}>
          Guardar lo corregido
        </Button>
        <Button size="sm" variant="ghost" onClick={alCancelar}>
          Cancelar
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="ml-auto text-muted-foreground"
          onClick={alMarcarIlegible}
          disabled={guardando}
        >
          No se puede leer
        </Button>
      </div>
    </div>
  );
}

function Respuesta({
  icono,
  titulo,
  nota,
  tecla,
  tono,
  alPulsar,
  inhabilitado,
}: {
  icono: React.ReactNode;
  titulo: string;
  nota: string;
  tecla: string;
  tono: "ok" | "bad" | "neutro";
  alPulsar: () => void;
  inhabilitado?: boolean;
}) {
  const estilo = {
    ok: "border-emerald-600/40 bg-emerald-600/10 text-emerald-700 hover:bg-emerald-600/20 dark:text-emerald-400",
    bad: "border-red-600/40 bg-red-600/5 text-red-700 hover:bg-red-600/15 dark:text-red-400",
    neutro: "border-border bg-card hover:bg-muted",
  }[tono];

  return (
    <button
      type="button"
      onClick={alPulsar}
      disabled={inhabilitado}
      className={cn(
        "flex items-start gap-3 rounded-lg border p-4 text-left transition-colors disabled:opacity-40",
        estilo,
      )}
    >
      {icono}
      <span className="min-w-0 flex-1">
        <span className="block font-semibold text-[14px]">{titulo}</span>
        <span className="mt-0.5 block text-[11.5px] text-muted-foreground">
          {nota}
        </span>
      </span>
      <kbd className="shrink-0 rounded border px-1.5 py-0.5 font-mono text-[10px]">
        {tecla}
      </kbd>
    </button>
  );
}

function Plegable({
  titulo,
  nota,
  abierto,
  alAbrir,
  children,
}: {
  titulo: string;
  nota: string;
  abierto: boolean;
  alAbrir: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-dashed">
      <button
        type="button"
        onClick={alAbrir}
        aria-expanded={abierto}
        className="flex w-full items-center gap-2 p-3 text-left text-[12px] transition-colors hover:bg-muted/50"
      >
        <ChevronRight
          className={cn(
            "size-3.5 shrink-0 text-muted-foreground transition-transform",
            abierto && "rotate-90",
          )}
        />
        <span>
          <b>{titulo}</b>{" "}
          <span className="text-muted-foreground">— {nota}</span>
        </span>
      </button>
      {abierto ? (
        <div className="border-t px-3 py-3 text-[12.5px]">{children}</div>
      ) : null}
    </div>
  );
}

function fecha(iso: string): string {
  const [a, m, d] = iso.split("-");
  return `${d}-${m}-${a}`;
}

/**
 * El puntaje de un candidato, de 0 a 100.
 *
 * El motor lo calcula de 0 a 1 —es una suma de pesos, y esa es su escala
 * natural—, pero contabilidad lo pidió «del 1 al 100» y tiene razón para la
 * pantalla: «0,65» invita a leerlo como una probabilidad, y no lo es. Un
 * entero sobre cien se lee como lo que es, una puntuación.
 *
 * La conversión vive aquí y no en el contrato a propósito: el número que se
 * guarda y se compara sigue siendo el del motor.
 */
function puntos(score: number): number {
  return Math.round(score * 100);
}
